<template>
    <div class="ui-page">
        <ui-page-header title="Colaboradores" subtitle="Equipe exibida no site, na ordem definida aqui.">
            <v-btn v-if="$allowed('create')" id="btnNew" type="button" color="primary" depressed
                to="/colaboradores/novo">
                <v-icon left small>mdi-plus</v-icon>
                Incluir
            </v-btn>
        </ui-page-header>

        <ui-table-card clearable :can-clear="hasFilters" @clear="clearFilters">
            <template #toolbar>
                <v-text-field dense id="name" placeholder="Buscar pelo nome" v-model="dataList.filter.name" hide-details
                    outlined prepend-inner-icon="mdi-magnify" class="ui-toolbar__search" @input="filterDebounced"
                    @keyup.enter="applyFilters"></v-text-field>
                <v-text-field dense id="email" placeholder="E-mail" v-model="dataList.filter.email" hide-details
                    outlined class="ui-toolbar__field" @input="filterDebounced" @keyup.enter="applyFilters"></v-text-field>
            </template>

            <v-data-table :headers="dataList.headers" :items="dataList.data"
                :server-items-length="dataList.count" disable-sort hide-default-footer :loading="dataList.loading"
                no-data-text="Nenhum colaborador foi encontrado" loading-text="Carregando dados..."
                :options.sync="dataList.options" class="ui-clickable-rows" @click:row="openRow">

                <template v-slot:[`item.created_at`]="{ item }">
                    {{ item.created_at | defaultDate("DD/MM/YYYY HH:mm") }}
                </template>

                <template v-slot:[`item.name`]="{ item }">
                    <div class="ui-cell-main">{{ item.name && item.name ? item.name : 'N/A' }}</div>
                    <div class="ui-cell-sub">{{ item.email && item.email ? item.email : 'N/A' }}</div>
                </template>

                <template v-slot:[`item.sort_order`]="{ item, index }">
                    <div class="d-flex align-center" style="gap: 4px;">
                        <v-tooltip bottom>
                            <template v-slot:activator="{ on, attrs }">
                                <v-btn
                                    icon
                                    small
                                    :disabled="!canMoveUp(index)"
                                    :loading="reorderingId === `${item.id}-up`"
                                    v-bind="attrs"
                                    v-on="on"
                                    @click.stop="reorderItem(item, 'up')">
                                    <v-icon size="18" color="#6B7280">mdi-arrow-up</v-icon>
                                </v-btn>
                            </template>
                            <span>Subir colaborador</span>
                        </v-tooltip>
                        <v-tooltip bottom>
                            <template v-slot:activator="{ on, attrs }">
                                <v-btn
                                    icon
                                    small
                                    :disabled="!canMoveDown(index)"
                                    :loading="reorderingId === `${item.id}-down`"
                                    v-bind="attrs"
                                    v-on="on"
                                    @click.stop="reorderItem(item, 'down')">
                                    <v-icon size="18" color="#6B7280">mdi-arrow-down</v-icon>
                                </v-btn>
                            </template>
                            <span>Descer colaborador</span>
                        </v-tooltip>
                    </div>
                </template>

                <template v-slot:[`item.actions`]="{ item }">
                    <ui-row-actions :actions="[
                        { text: 'Editar', icon: 'mdi-pencil-outline', disabled: !$check('Employee', 'PATCH'), handler: () => viewItem(item) },
                        { text: 'Excluir', icon: 'mdi-delete-outline', danger: true, disabled: !$check('Employee', 'DELETE'), handler: () => deleteItem(item) },
                    ]" />
                </template>
            </v-data-table>

            <template #footer>
                <ui-pagination :total="dataList.count" :page="dataList.options.page" :items-per-page="dataList.options.size"
                    :disabled="dataList.loading" @update:page="pageChanged" @update:itemsPerPage="setPageSize" />
            </template>
        </ui-table-card>
    </div>
</template>

<script>

import Vue from "vue";
import VueLodash from 'vue-lodash'
import _ from 'lodash';
import axios from 'axios';

import object from "@/common/util/object"

Vue.use(VueLodash)

export default {
    name: "EmployeeList",

    data: () => ({
        dataset: {
            data: {}
        },
        dataList: {
            filter: {
                name: "",
                email: "",
            },
            headers: [
                { text: "Ordem", value: "sort_order", sortable: false },
                { text: "Nome", value: "name" },
                { text: "Criado em", value: "created_at" },
                { text: '', value: 'actions', sortable: false, align: 'end', width: 56 },
            ],
            data: [],
            loading: false,
            count: 0,
            options: {
                page: 1,
                size: 10,
                sortBy: ['name'],
                sortDesc: [false],
            },
            footerOptions: {
                itemsPerPageText: 'Itens por página:',
                itemsPerPageOptions: [10, 20, 30, 40, 50],
                pageText: '{0}-{1} de {2}',
            }
        },
        reorderingId: null,
    }),

    computed: {
        hasFilters() {
            return Object.values(this.dataList.filter).some((value) => value);
        },
    },

    created() {
        this.load();
    },

    methods: {
        applyFilters() {
            this.dataList.options.page = 1;
            this.load();
        },

        filterDebounced: _.debounce(function () {
            this.applyFilters();
        }, 400),

        clearFilters() {
            this.dataList.filter = { name: "", email: "" };
            this.applyFilters();
        },

        // A troca de tamanho e seguida de update:page(1), que recarrega.
        setPageSize(size) {
            this.dataList.options.size = size;
        },

        pageChanged(page) {            
            this.dataList.options.page = page;
            this.load();
        },

        pageSizeChanged(size) {    
            this.dataList.options.size = size;
            this.load(true);
        },

        getColorByStatus(status) {
            switch (status) {
                case 0:
                    return 'warning'
                case 1:
                    return 'accent'
                case 2:
                    return 'error'
                default:
                    break;
            }
        },

        debounce: _.debounce(function () {
            this.reload();
        }, 500),

        reload: function () {
            this.load();
        },

        canMoveUp(index) {
            return this.$check('Employee', 'PATCH') && (this.dataList.options.page > 1 || index > 0);
        },

        canMoveDown(index) {
            return this.$check('Employee', 'PATCH') && (index < this.dataList.data.length - 1 || this.dataList.options.page * this.dataList.options.size < this.dataList.count);
        },

        async reorderItem(item, direction) {
            if (!item || !item.id || !direction) return;

            this.reorderingId = `${item.id}-${direction}`;

            try {
                await axios.patch(`/api/admin/employees/${item.id}/reorder`, { direction });
                await this.load();
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), { position: 'top-right', timeout: 5000 });
            } finally {
                this.reorderingId = null;
            }
        },

        async load() {

            let search = Object.fromEntries(Object.entries(this.dataList.filter).filter(([_, v]) => v))

            search = !object.isEmpty(search) ? search : null;

            this.dataList.loading = true;

            var limit = this.dataList.options.size;
    
            try {
                const result = await axios.get('/api/employees', { params: { page: this.dataList.options.page, limit: limit, search: search } });

                this.dataList.data = result.data.content.data;
                this.dataList.count = result.data.content.paging.total;

            } catch (error) {
                console.log('error ', error)
                this.$dialog.notify.error(this.$codes(error), { position: 'top-right', timeout: 5000 });
            } finally {
                this.dataList.loading = false;
            }

        },

        // A linha abre o detalhe so com a mesma permissao do "Editar".
        openRow(item) {
            if (this.$check('Employee', 'GET')) this.viewItem(item);
        },

        viewItem(item) {
            this.$router.push(`colaboradores/${item.id}`);
        },

        async deleteItem(item) {

            if (!item || !item.id) return;

            const proceedAction = await this.$dialog.confirm({
                text: 'Tem certeza que deseja continuar?',
                title: 'Excluir Colaborador',
                actions: {
                    false: 'Cancelar',
                    true: 'Excluir'
                }
            })

            if (!proceedAction) return;

            this.dataList.loading = true

            try {

                const result = await axios.delete(`/api/admin/employees/${item.id}`)

                this.$dialog.notify.info("Colaborador excluído com sucesso", { position: "top-right", timeout: 2000 });

                this.load()

            } catch (error) {

                this.$dialog.notify.error(this.$codes(error), {
                    position: 'top-right',
                    timeout: 2000
                })

            } finally {
                this.dataList.loading = false;
            }

        },
    },
};
</script>
